import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { X, ArrowRight, ArrowLeft, SkipForward, CheckCircle2, GraduationCap, Loader2 } from "lucide-react";
import { toast } from "sonner";

/* --- Role-based tutorial steps ---
   Each step: { target?: CSS selector to highlight, title, body, path?: route to navigate to,
                waitFor?: selector that must exist before next step, nextLabel?: string }
   If `target` is omitted the step shows a centered modal.
*/

const STEPS_CLIENTE = [
  { path: "/", title: "Benvenuto in Energy World!",
    body: "Ti mostro in 1 minuto come richiedere un preventivo e seguire le tue richieste. Puoi uscire quando vuoi — i dati di esempio verranno rimossi.",
    nextLabel: "Partiamo" },
  { path: "/", target: "[data-testid=\"nav-logo\"]",
    title: "Il tuo cruscotto cliente",
    body: "In alto a destra trovi il menu profilo. Da lì accedi alle tue richieste e al tutorial." },
  { path: "/", target: "a[href=\"/#servizi\"]",
    title: "Scegli il servizio",
    body: "Dalla home scorri fino ai servizi (climatizzazione, fotovoltaico, impianti elettrici, ecc.) oppure clicca qui per vederli tutti." },
  { path: "/dashboard", target: "[data-testid=\"user-dashboard\"], h1",
    title: "Le tue richieste",
    body: "Qui vedi tutti i preventivi che hai richiesto, con lo stato (Nuovo, In lavorazione, Chiuso)." },
  { path: "/dashboard", target: "[data-testid^=\"quote-card\"], .quote-card, a[href^=\"/dashboard\"]",
    title: "Chatta con il personale",
    body: "Apri un preventivo per leggere il riepilogo e chattare direttamente con il nostro personale. Rispondiamo in giornata." },
  { title: "Tutorial completato!",
    body: "Perfetto! Rimuoviamo i dati di esempio e sei pronto a usare Energy World. Puoi rivedere il tutorial dal tuo profilo in qualsiasi momento.",
    final: true },
];

const STEPS_OPERAIO = [
  { title: "Benvenuto operaio!",
    body: "Ti mostro in 1 minuto come usare il tablet del furgone: lavori assegnati, ore, materiali e firma del cliente.",
    nextLabel: "Partiamo" },
  { path: "/lavori", target: "h1, [data-testid=\"jobs-list\"]",
    title: "I tuoi lavori",
    body: "Qui vedi tutti i lavori del tuo furgone. Entra in uno per aprire il rapportino di lavoro (RdL)." },
  { path: "/lavori", target: "[data-testid^=\"job-row\"], a[href^=\"/lavori/\"]",
    title: "Apri un lavoro",
    body: "Clicca su un lavoro per vederne i dettagli e le relazioni (RdL). Il lavoro di esempio è contrassegnato [TUTORIAL]." },
  { title: "Nel RdL puoi inserire…",
    body: "• Dati del cliente e descrizione intervento\n• Ore lavorate (anche per i colleghi del furgone)\n• Materiali usati con autocompletamento dal catalogo prezziario\n• Firma del cliente per chiudere la relazione",
    nextLabel: "Chiaro" },
  { title: "Tutorial completato!",
    body: "Rimuoviamo i dati di esempio. Puoi rivedere il tutorial dal tuo profilo quando vuoi.",
    final: true },
];

const STEPS_STAFF = [
  { title: "Benvenuto nello staff!",
    body: "In 2 minuti vediamo insieme la dashboard, i preventivi clienti, i preventivi staff, mailing, furgoni e catalogo materiali.",
    nextLabel: "Partiamo" },
  { path: "/admin", target: "h1, [data-testid=\"admin-dashboard\"]",
    title: "Dashboard",
    body: "KPI, grafici per periodo, categoria e città. Il punto di partenza per vedere l'andamento." },
  { path: "/admin/preventivi", target: "h1",
    title: "Preventivi dai clienti",
    body: "Tutte le richieste arrivate dalla chat IA. Puoi cambiarne stato, categoria, città e chattare col cliente." },
  { path: "/admin/preventivi-staff", target: "h1",
    title: "Preventivi staff (Blocco B)",
    body: "Crei preventivi internamente con la chat IA multi-turno. L'IA fa le domande e genera il PDF brandizzato." },
  { path: "/admin/mailing", target: "h1",
    title: "Mailing list segmentata",
    body: "Campagne email con editor rich-text, segmentazione cliente/staff/opt-in e analytics di invio." },
  { path: "/admin/furgoni", target: "h1",
    title: "Furgoni e PWA",
    body: "Associa operai (tablet condiviso) o personale/admin (cellulari personali al link /mobile). Genera il codice a 6 cifre per associare un dispositivo." },
  { path: "/admin/catalogo-materiali", target: "h1",
    title: "Catalogo materiali",
    body: "Database degli articoli riutilizzabili con prezzo. Gli operai li ritrovano nei rapportini via autocompletamento." },
  { title: "Tutorial completato!",
    body: "Rimuoviamo i dati di esempio generati. Puoi rilanciare il tour dal tuo profilo in qualsiasi momento.",
    final: true },
];

function stepsFor(role) {
  if (role === "cliente") return STEPS_CLIENTE;
  if (role === "operaio") return STEPS_OPERAIO;
  if (role === "personale" || role === "admin") return STEPS_STAFF;
  return [];
}

const TutorialCtx = createContext(null);
export const useTutorial = () => useContext(TutorialCtx);

export function TutorialProvider({ children }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [starting, setStarting] = useState(false);
  const [targetRect, setTargetRect] = useState(null);
  const promptedKeyRef = useRef(null);

  const steps = useMemo(() => stepsFor(user?.role), [user]);
  const current = steps[idx] || null;

  // Auto-prompt first-time users (not for guests, not during OAuth)
  useEffect(() => {
    if (!user) return;
    if (user.must_change_password) return;
    if (user.tutorial_completed) return;
    if (active) return;
    // Only prompt once per browser session
    const key = `tut.prompted.${user.user_id}`;
    if (sessionStorage.getItem(key)) return;
    if (promptedKeyRef.current === key) return;
    promptedKeyRef.current = key;
    sessionStorage.setItem(key, "1");
    // Don't auto-prompt on tablet-paired PWA entry pages
    if (location.pathname.startsWith("/tablet") || location.pathname.startsWith("/mobile")) return;
    // Dismiss any previous toasts, show a small invite
    toast(
      <div className="flex items-start gap-2">
        <GraduationCap className="w-5 h-5 text-blue-700 mt-0.5"/>
        <div>
          <div className="font-semibold text-slate-900">Nuovo da queste parti?</div>
          <div className="text-sm text-slate-600">Fai un tour guidato di 1 minuto.</div>
          <div className="flex gap-2 mt-2">
            <button onClick={() => { toast.dismiss(); start(); }}
                    className="text-xs bg-blue-700 text-white rounded px-2 py-1 hover:bg-blue-800"
                    data-testid="tut-invite-start">Inizia tour</button>
            <button onClick={async () => { toast.dismiss(); try { await api.post("/tutorial/skip"); await refresh(); } catch {} }}
                    className="text-xs text-slate-500 hover:text-slate-700"
                    data-testid="tut-invite-skip">Non ora</button>
          </div>
        </div>
      </div>,
      { duration: 20000, id: "tut-invite" }
    );
    // eslint-disable-next-line
  }, [user, location.pathname]);

  // Dismiss any lingering invite as soon as the tour becomes active
  useEffect(() => { if (active) toast.dismiss("tut-invite"); }, [active]);

  const start = useCallback(async () => {
    if (!user) return;
    setStarting(true);
    try {
      toast.dismiss("tut-invite");
      await api.post("/tutorial/start");
      setIdx(0);
      setActive(true);
      // Navigate to first step's path if any
      const s = steps[0];
      if (s?.path && location.pathname !== s.path) navigate(s.path);
    } catch (e) {
      toast.error("Impossibile avviare il tutorial");
    } finally { setStarting(false); }
  }, [user, steps, navigate, location.pathname]);

  const finish = useCallback(async (skipped) => {
    try {
      await api.post(skipped ? "/tutorial/skip" : "/tutorial/complete");
      await refresh();
    } catch { /* ignore */ }
    setActive(false);
    setIdx(0);
    toast.success(skipped ? "Tutorial saltato — dati di esempio rimossi" : "Tutorial completato");
  }, [refresh]);

  const next = useCallback(() => {
    if (!current) return;
    if (current.final) { finish(false); return; }
    const n = steps[idx + 1];
    if (!n) { finish(false); return; }
    if (n.path && location.pathname !== n.path) navigate(n.path);
    setIdx(idx + 1);
  }, [current, idx, steps, location.pathname, navigate, finish]);

  const prev = useCallback(() => {
    if (idx === 0) return;
    const p = steps[idx - 1];
    if (p?.path && location.pathname !== p.path) navigate(p.path);
    setIdx(idx - 1);
  }, [idx, steps, location.pathname, navigate]);

  // Recompute target rect for the spotlight on each step / resize / scroll
  useEffect(() => {
    if (!active || !current?.target) { setTargetRect(null); return; }
    let frame;
    const compute = () => {
      // multiple selectors supported, pick the first that resolves
      const selectors = (current.target || "").split(",").map(s => s.trim()).filter(Boolean);
      let el = null;
      for (const sel of selectors) {
        try { el = document.querySelector(sel); } catch { el = null; }
        if (el) break;
      }
      if (!el) { setTargetRect(null); return; }
      const r = el.getBoundingClientRect();
      setTargetRect({
        top: r.top + window.scrollY,
        left: r.left + window.scrollX,
        width: r.width,
        height: r.height,
      });
      try { el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" }); } catch {}
    };
    const run = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(compute); };
    // Give the page a moment to render after route changes
    const t1 = setTimeout(run, 350);
    const t2 = setTimeout(run, 900);
    window.addEventListener("resize", run);
    window.addEventListener("scroll", run, true);
    return () => {
      clearTimeout(t1); clearTimeout(t2);
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", run);
      window.removeEventListener("scroll", run, true);
    };
  }, [active, current, idx, location.pathname]);

  return (
    <TutorialCtx.Provider value={{ active, start, finish, starting,
                                   currentIndex: idx, totalSteps: steps.length }}>
      {children}
      {active && current && (
        <TutorialOverlay
          step={current}
          index={idx}
          total={steps.length}
          targetRect={targetRect}
          onNext={next}
          onPrev={prev}
          onSkip={() => finish(true)}
        />
      )}
    </TutorialCtx.Provider>
  );
}

function TutorialOverlay({ step, index, total, targetRect, onNext, onPrev, onSkip }) {
  const [pos, setPos] = useState({ top: 120, left: 24 });
  const popRef = useRef(null);

  useEffect(() => {
    const vp = { w: window.innerWidth, h: window.innerHeight };
    const popW = Math.min(380, vp.w - 32);
    const popH = popRef.current?.offsetHeight || 240;
    if (!targetRect) {
      setPos({ top: Math.max(80, vp.h / 2 - popH / 2 + window.scrollY), left: Math.max(16, vp.w / 2 - popW / 2) });
      return;
    }
    const screenTop = targetRect.top - window.scrollY;
    const spaceBelow = vp.h - (screenTop + targetRect.height);
    const placeBelow = spaceBelow > popH + 24;
    const top = placeBelow
      ? targetRect.top + targetRect.height + 14
      : Math.max(window.scrollY + 20, targetRect.top - popH - 14);
    let left = targetRect.left + targetRect.width / 2 - popW / 2;
    left = Math.max(16, Math.min(left, vp.w - popW - 16));
    setPos({ top, left });
  }, [targetRect, index]);

  // SVG mask spotlight
  const spot = targetRect ? (() => {
    const pad = 8;
    const r = {
      top: Math.max(0, targetRect.top - pad),
      left: Math.max(0, targetRect.left - pad),
      width: targetRect.width + pad * 2,
      height: targetRect.height + pad * 2,
    };
    return r;
  })() : null;

  return (
    <>
      {/* Backdrop with spotlight hole */}
      <div className="fixed inset-0 pointer-events-none z-[9998]">
        <svg width="100%" height="100%" style={{ position: "fixed", inset: 0, pointerEvents: "none" }}>
          <defs>
            <mask id="tut-mask">
              <rect width="100%" height="100%" fill="white"/>
              {spot && (
                <rect x={spot.left - window.scrollX} y={spot.top - window.scrollY}
                      width={spot.width} height={spot.height} rx="12" ry="12" fill="black"/>
              )}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(9, 14, 30, 0.72)" mask="url(#tut-mask)"/>
          {spot && (
            <rect x={spot.left - window.scrollX} y={spot.top - window.scrollY}
                  width={spot.width} height={spot.height} rx="12" ry="12"
                  fill="none" stroke="#60a5fa" strokeWidth="2"
                  style={{ filter: "drop-shadow(0 0 12px rgba(96,165,250,0.75))" }}/>
          )}
        </svg>
      </div>
      {/* Popover */}
      <div ref={popRef}
           className="z-[9999] fixed w-[360px] max-w-[calc(100vw-32px)] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
           style={{ top: pos.top - window.scrollY, left: pos.left }}
           data-testid="tutorial-popover">
        <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4"/>
            <span className="text-xs uppercase tracking-widest font-semibold">Tutorial · {index + 1}/{total}</span>
          </div>
          <button onClick={onSkip} className="p-1 rounded hover:bg-white/20"
                  title="Chiudi tutorial" data-testid="tut-close">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="p-4">
          <h3 className="font-bold text-slate-900 text-lg mb-1 heading" data-testid="tut-title">{step.title}</h3>
          <p className="text-sm text-slate-600 whitespace-pre-line" data-testid="tut-body">{step.body}</p>
        </div>
        <div className="px-4 pb-4 flex items-center justify-between gap-2">
          <button onClick={onSkip} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                  data-testid="tut-skip">
            <SkipForward className="w-3.5 h-3.5"/> Salta tour
          </button>
          <div className="flex items-center gap-2">
            {index > 0 && !step.final && (
              <button onClick={onPrev}
                      className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1.5 rounded"
                      data-testid="tut-prev">
                <ArrowLeft className="w-4 h-4"/> Indietro
              </button>
            )}
            <button onClick={onNext}
                    className="text-sm bg-blue-800 hover:bg-blue-900 text-white font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1"
                    data-testid="tut-next">
              {step.final ? (<><CheckCircle2 className="w-4 h-4"/> Fine</>) :
                <>{step.nextLabel || "Avanti"} <ArrowRight className="w-4 h-4"/></>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
